import * as Page from '../../components/content/page-header';
import { Content, RightActions } from '../../components/layout/content';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { Table } from '../../components/table/table';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Dialog, Title, Content as DialogContent } from '../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Radio } from '../../components/forms/radio';
import { Theme as ThemeType } from './types/theme';
import { useQuery } from '../../hooks/use-query';
import { AppSettings } from './types/app-settings';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { ActionColumn } from '../../components/table/columns';

export const Theme = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit } = useForm();
  const { register: registerCreate, handleSubmit: handleCreateSubmit, reset: resetCreate, formState: { errors } } = useForm();
  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const themes = useQuery<ThemeType[]>({ path: '/theme' });
  const appSettings = useQuery<AppSettings>({ path: '/appsettings' });
  const postMutation = usePostMutation({ path: '/theme', cacheKey: '/theme' });
  const deleteMutation = useDeleteMutation({ cacheKey: '/theme' });
  const putMutation = usePutMutation({ path: `/appsettings/default/theme/${selectedTheme}` });

  useEffect(() => {
    if (appSettings.data?.defaultTheme) {
      setSelectedTheme(appSettings.data?.defaultTheme);
    }
  }, [appSettings.data?.defaultTheme]);

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSelectedTheme(event.target.value);
  };

  const columns = [
    { accessor: 'slug', Header: 'Name', sort: true },
    { accessor: 'isDefault', Header: 'Is Default', Cell: (item: any): JSX.Element => {
      const { id } = item.row.original;
      return <div className="d-flex">
        <Radio {...register('isDefault')} value={id} currentValue={selectedTheme === id ? id : ''} hideLabel onChange={onChange} />
      </div>;
    } },
    ActionColumn({ onDelete: (item: ThemeType): void => setPageState(Modes.DELETE, item) })
  ];

  const onCreate = (data: FieldValues): void => {
    setPageState(Modes.BROWSE);
    postMutation.mutate(data);
    resetCreate();
  };

  const onUpdate = (): void => {
    putMutation.mutate(selectedTheme);
  };

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/theme/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  return <>
    <RightActions>
      <Button.Primary type="submit" form="update-form" disabled={putMutation.isLoading}>
        Update
      </Button.Primary>
      <Button.Tertiary disabled={putMutation.isLoading}>
        Cancel
      </Button.Tertiary>
    </RightActions>
    <Content>
      <Page.Header>
        <div className="mb-2">
          <Page.Title>Theme</Page.Title>
        </div>
        <Page.SubTitle>
          Configure themes available for DisasterAWARE.
          <Button.LightBlackSmall onClick={(): void => setPageState(Modes.CREATE)}>
            <Icons.Add />
            Add Theme
          </Button.LightBlackSmall>
        </Page.SubTitle>
      </Page.Header>
      <form id="update-form" onSubmit={handleSubmit(onUpdate)}>
        <Table columns={columns} rows={themes.data || []} />
      </form>
    </Content>
    {pageState.mode === Modes.CREATE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleCreateSubmit(onCreate)}>
          <Title>Add Theme</Title>
          <Content>
            <div className="mb-4">
              <Label name="slug">Name</Label>
              <TextInputLarge {...registerCreate('slug', { required: 'You must provide a value for Name.' })} errors={errors} autoFocus />
            </div>
          </Content>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              <Icons.Add /> Add Theme
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Theme</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};

